import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  ArrowLeft, FileText, Clock, DollarSign, Users, 
  Package, Calendar, AlertTriangle, Receipt, Plus, Edit, Trash2,
  Upload, Download, Settings, Briefcase, TrendingUp, MessageSquare,
  Shield, Camera, ClipboardList, Truck, Building, Eye, Palette,
  FolderOpen, Image, Video, MapPin, ChevronDown, ChevronRight, X, File, CheckCircle2,
  Lock, Printer
} from "lucide-react";
import { toast } from "sonner";

// Import extracted components for better organization
import { BasicDetailsContent } from "@/components/project-detail/BasicDetailsContent";
import { OtherDocumentsContent } from "@/components/project-detail/OtherDocumentsContent";
import { VariationsContent } from "@/components/project-detail/VariationsContent";
import { PaymentsContent } from "@/components/project-detail/PaymentsContent";
import { LegalDocumentsContent } from "@/components/project-detail/LegalDocumentsContent";
import { TimelineContent } from "@/components/project-detail/TimelineContent";
import { VendorsContent } from "@/components/project-detail/VendorsContent";
import { MaterialsContent } from "@/components/project-detail/MaterialsContent";
import { BOQContent } from "@/components/project-detail/BOQContent";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  "Planning": "bg-blue-100 text-blue-700",
  "In Progress": "bg-blue-100 text-blue-700",
  "On Hold": "bg-amber-100 text-amber-700",
  "Completed": "bg-emerald-100 text-emerald-700",
  "Delayed": "bg-red-100 text-red-700",
  "Draft": "bg-slate-100 text-slate-700",
  "Submitted": "bg-blue-100 text-blue-700",
  "Approved": "bg-emerald-100 text-emerald-700",
  "Rejected": "bg-red-100 text-red-700",
  "Pending": "bg-amber-100 text-amber-700",
  "Paid": "bg-emerald-100 text-emerald-700",
  "Partial": "bg-amber-100 text-amber-700",
  "Unpaid": "bg-red-100 text-red-700",
  "Open": "bg-red-100 text-red-700",
  "Mitigating": "bg-amber-100 text-amber-700",
  "Resolved": "bg-emerald-100 text-emerald-700",
  "Closed": "bg-slate-100 text-slate-700",
  "Active": "bg-emerald-100 text-emerald-700",
};

// Module card configuration
const moduleCards = [
  { id: "basic", name: "Basic Details", icon: Settings, color: "from-blue-400 to-blue-600", bgColor: "bg-blue-50" },
  { id: "design", name: "Design Documents", icon: Palette, color: "from-fuchsia-400 to-fuchsia-600", bgColor: "bg-fuchsia-50" },
  { id: "documents", name: "Other Documents", icon: FileText, color: "from-purple-400 to-purple-600", bgColor: "bg-purple-50" },
  { id: "boq", name: "BOQ & Quotations", icon: Receipt, color: "from-pink-400 to-pink-600", bgColor: "bg-pink-50" },
  { id: "legal", name: "Legal Documents", icon: Shield, color: "from-indigo-400 to-indigo-600", bgColor: "bg-indigo-50" },
  { id: "timeline", name: "Timeline & Milestones", icon: Clock, color: "from-orange-400 to-orange-600", bgColor: "bg-orange-50" },
  { id: "payments", name: "Payments & Finance", icon: DollarSign, color: "from-green-400 to-green-600", bgColor: "bg-green-50" },
  { id: "vendors", name: "Vendors", icon: Users, color: "from-cyan-400 to-cyan-600", bgColor: "bg-cyan-50" },
  { id: "materials", name: "Materials & Procurement", icon: Package, color: "from-amber-400 to-amber-600", bgColor: "bg-amber-50" },
  { id: "progress", name: "Site Progress", icon: TrendingUp, color: "from-teal-400 to-teal-600", bgColor: "bg-teal-50" },
  { id: "risks", name: "Risk Management", icon: AlertTriangle, color: "from-red-400 to-red-600", bgColor: "bg-red-50" },
];

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { token, hasPermission, user } = useAuth();
  const [project, setProject] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeModule, setActiveModule] = useState(null);
  const [moduleData, setModuleData] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [moduleAccess, setModuleAccess] = useState({ has_full_access: true, modules: [] });
  const [progressReportOpen, setProgressReportOpen] = useState(false);
  const [progressReportData, setProgressReportData] = useState(null);

  const canWrite = hasPermission("projects:write");
  const isAdmin = user?.role === 'Admin';

  useEffect(() => {
    fetchProjectData();
    fetchModuleAccess();
  }, [projectId]);

  const fetchModuleAccess = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/my-modules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setModuleAccess(response.data);
    } catch (error) {
      console.error("Failed to fetch module access");
    }
  };

  const canAccessModule = (moduleId) => {
    if (isAdmin || moduleAccess.has_full_access) return true;
    return moduleAccess.modules.includes(moduleId);
  };

  const handleModuleClick = async (moduleId) => {
    if (!canAccessModule(moduleId)) {
      toast.error("You don't have access to this module. Please contact an administrator.");
      return;
    }
    await openModule(moduleId);
  };

  useEffect(() => {
    fetchProjectData();
  }, [projectId]);

  const fetchProjectData = async () => {
    try {
      const [projectRes, summaryRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/projects/${projectId}/summary`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setProject(projectRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Failed to fetch project");
      navigate("/projects");
    } finally {
      setLoading(false);
    }
  };

  const generateProgressReport = async () => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      // Fetch all necessary data for the progress report
      const [milestonesRes, expensesRes, risksRes, progressRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/milestones`, { headers }),
        axios.get(`${API}/projects/${projectId}/expenses`, { headers }),
        axios.get(`${API}/projects/${projectId}/risks`, { headers }),
        axios.get(`${API}/projects/${projectId}/site-progress`, { headers })
      ]);

      const milestones = milestonesRes.data || [];
      const expenses = expensesRes.data || [];
      const risks = risksRes.data || [];
      const siteProgress = progressRes.data || [];

      // Calculate statistics
      const completedMilestones = milestones.filter(m => m.status === 'Completed').length;
      const totalExpenses = expenses.reduce((sum, e) => sum + (e.amount || 0), 0);
      const openRisks = risks.filter(r => r.status === 'Open' || r.status === 'Mitigating').length;
      const avgCompletion = milestones.length > 0 
        ? Math.round(milestones.reduce((sum, m) => sum + (m.completion_percent || 0), 0) / milestones.length) 
        : project?.completion || 0;

      setProgressReportData({
        milestones,
        expenses,
        risks,
        siteProgress,
        stats: {
          completedMilestones,
          totalMilestones: milestones.length,
          totalExpenses,
          budget: summary?.budget || 0,
          openRisks,
          totalRisks: risks.length,
          avgCompletion
        }
      });
      setProgressReportOpen(true);
    } catch (error) {
      toast.error("Failed to generate progress report");
    }
  };

  const fetchModuleData = async (moduleId) => {
    const headers = { Authorization: `Bearer ${token}` };
    try {
      let data = {};
      switch (moduleId) {
        case "design":
          const [designDocsRes, categoriesRes] = await Promise.all([
            axios.get(`${API}/projects/${projectId}/design-documents`, { headers }),
            axios.get(`${API}/projects/${projectId}/design-documents/categories`, { headers })
          ]);
          data = { designDocuments: designDocsRes.data, categories: categoriesRes.data };
          break;
        case "documents":
          const docsRes = await axios.get(`${API}/projects/${projectId}/documents`, { headers });
          data = { documents: docsRes.data };
          break;
        case "boq":
          const [boqRes, qtRes] = await Promise.all([
            axios.get(`${API}/projects/${projectId}/boq`, { headers }),
            axios.get(`${API}/quotations/project/${projectId}`, { headers })
          ]);
          data = { boqs: boqRes.data, quotations: qtRes.data };
          break;
        case "timeline":
          const msRes = await axios.get(`${API}/projects/${projectId}/milestones`, { headers });
          data = { milestones: msRes.data };
          break;
        case "payments":
          const [expRes, invRes, payRes] = await Promise.all([
            axios.get(`${API}/projects/${projectId}/expenses`, { headers }),
            axios.get(`${API}/projects/${projectId}/invoices`, { headers }),
            axios.get(`${API}/projects/${projectId}/payments`, { headers })
          ]);
          data = { expenses: expRes.data, invoices: invRes.data, payments: payRes.data };
          break;
        case "vendors":
          const vendorsRes = await axios.get(`${API}/projects/${projectId}/vendors`, { headers });
          data = { vendors: vendorsRes.data };
          break;
        case "materials":
          const matRes = await axios.get(`${API}/projects/${projectId}/materials`, { headers });
          data = { materials: matRes.data };
          break;
        case "progress":
          const progressRes = await axios.get(`${API}/projects/${projectId}/site-progress`, { headers });
          data = { siteProgress: progressRes.data };
          break;
        case "legal":
          const [vendorContractsRes, clientAgreementsRes, completionChecklistsRes] = await Promise.all([
            axios.get(`${API}/legal-documents/vendor-contracts/project/${projectId}`, { headers }),
            axios.get(`${API}/legal-documents/client-agreements/project/${projectId}`, { headers }),
            axios.get(`${API}/legal-documents/completion-checklists/project/${projectId}`, { headers })
          ]);
          data = { vendorContracts: vendorContractsRes.data, clientAgreements: clientAgreementsRes.data, completionChecklists: completionChecklistsRes.data };
          break;
        case "risks":
          const riskRes = await axios.get(`${API}/projects/${projectId}/risks`, { headers });
          data = { risks: riskRes.data };
          break;
        default:
          break;
      }
      setModuleData(data);
    } catch (error) {
      toast.error(`Failed to fetch ${moduleId} data`);
    }
  };

  const openModule = async (moduleId) => {
    setActiveModule(moduleId);
    if (moduleId !== "basic") {
      await fetchModuleData(moduleId);
    }
  };

  const closeModule = () => {
    setActiveModule(null);
    setModuleData({});
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brillien-600"></div>
      </div>
    );
  }

  if (!project) return null;

  return (
    <div className="p-8 bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/projects")}
            className="text-slate-600 hover:text-brillien-600"
            data-testid="back-to-projects"
          >
            <ArrowLeft className="h-5 w-5 mr-2" />
            Back to Projects
          </Button>
        </div>
        <Button 
          onClick={generateProgressReport} 
          className="bg-brillien-600 hover:bg-brillien-700 text-white" 
          data-testid="view-progress-report"
        >
          <Eye className="h-4 w-4 mr-2" />
          View Progress Report
        </Button>
      </div>

      {/* Project Info Card */}
      <Card className="p-6 mb-8 border-0 shadow-lg rounded-2xl bg-white">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <span className="font-mono text-sm text-slate-500 bg-slate-100 px-2 py-1 rounded">ID: {project.id}</span>
              <Badge className={`${statusColors[project.status]} border-0 font-medium`}>{project.status}</Badge>
            </div>
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{project.name}</h1>
            <p className="text-slate-600">Client: <span className="font-medium text-slate-800">{project.client}</span></p>
            <p className="text-sm text-slate-500 mt-2">
              {project.city}, {project.state} • {project.area} • PM: {project.pm}
            </p>
          </div>
          
          <div className="flex-1 max-w-md">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Actual Progress</p>
                <Progress value={project.completion || 0} className="h-3 rounded-full" />
                <p className="text-2xl font-bold text-slate-900 mt-2">{project.completion || 0}%</p>
                <p className="text-xs text-slate-500">From site updates</p>
              </div>
              <div>
                <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Estimated Progress</p>
                <Progress value={project.estimated_progress || 0} className="h-3 rounded-full bg-slate-200" />
                <p className="text-2xl font-bold text-slate-900 mt-2">{project.estimated_progress || 0}%</p>
                <p className="text-xs text-slate-500">From milestones</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mt-6 pt-6 border-t border-slate-100">
            <div className="text-center" data-testid="stat-budget" title="Budget from Basic Details">
              <p className="text-2xl font-bold text-slate-900">₹{((summary.budget || 0) / 100000).toFixed(1)}L</p>
              <p className="text-xs text-slate-500 uppercase">Budget</p>
            </div>
            <div className="text-center" data-testid="stat-expenses" title="Expenses from Payments & Finance">
              <p className="text-2xl font-bold text-orange-600">₹{((summary.total_expenses || 0) / 100000).toFixed(1)}L</p>
              <p className="text-xs text-slate-500 uppercase">Expenses</p>
            </div>
            <div className="text-center" data-testid="stat-invoiced" title="Total Invoiced from Payments & Finance">
              <p className="text-2xl font-bold text-blue-600">₹{((summary.total_invoiced || 0) / 100000).toFixed(1)}L</p>
              <p className="text-xs text-slate-500 uppercase">Invoiced</p>
            </div>
            <div className="text-center" data-testid="stat-received" title="Received Payments from Invoices">
              <p className="text-2xl font-bold text-emerald-600">₹{((summary.total_received || 0) / 100000).toFixed(1)}L</p>
              <p className="text-xs text-slate-500 uppercase">Received</p>
            </div>
            <div className="text-center" data-testid="stat-milestones" title="Milestones from Timeline & Milestones">
              <p className="text-2xl font-bold text-slate-900">{summary.milestones_completed}/{summary.milestones_total}</p>
              <p className="text-xs text-slate-500 uppercase">Milestones</p>
            </div>
            <div className="text-center" data-testid="stat-risks" title="Open Risks from Risk Management">
              <p className="text-2xl font-bold text-red-600">{summary.open_risks || 0}</p>
              <p className="text-xs text-slate-500 uppercase">Open Risks</p>
            </div>
          </div>
        )}
      </Card>

      {/* Module Cards Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {moduleCards.map((module) => {
          const Icon = module.icon;
          const hasAccess = canAccessModule(module.id);
          return (
            <Card
              key={module.id}
              className={`p-6 border-0 shadow-md rounded-2xl cursor-pointer transition-all duration-300 relative ${
                hasAccess 
                  ? `${module.bgColor} hover:shadow-xl hover:scale-105` 
                  : 'bg-slate-100 opacity-75'
              }`}
              onClick={() => handleModuleClick(module.id)}
              data-testid={`module-card-${module.id}`}
            >
              {/* Lock overlay for non-accessible modules */}
              {!hasAccess && (
                <div className="absolute inset-0 bg-slate-200/50 rounded-2xl flex items-center justify-center z-10">
                  <div className="bg-slate-700 p-2 rounded-full">
                    <Lock className="h-5 w-5 text-white" />
                  </div>
                </div>
              )}
              <div className="flex flex-col items-center text-center">
                <div className={`w-16 h-16 rounded-2xl ${
                  hasAccess 
                    ? `bg-gradient-to-br ${module.color}` 
                    : 'bg-slate-400'
                } flex items-center justify-center mb-4 shadow-lg`}>
                  <Icon className="h-8 w-8 text-white" strokeWidth={1.5} />
                </div>
                <h3 className={`text-sm font-semibold leading-tight ${hasAccess ? 'text-slate-800' : 'text-slate-500'}`}>
                  {module.name}
                </h3>
                {!hasAccess && (
                  <p className="text-xs text-slate-400 mt-1">Locked</p>
                )}
              </div>
            </Card>
          );
        })}
      </div>

      {/* Module Dialogs */}
      <Dialog open={activeModule === "basic"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              Basic Details
            </DialogTitle>
          </DialogHeader>
          <BasicDetailsContent project={project} token={token} user={user} onProjectUpdate={fetchProjectData} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "design"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Palette className="h-5 w-5 text-fuchsia-600" />
              Design Documents
            </DialogTitle>
          </DialogHeader>
          <DesignDocumentsContent 
            projectId={projectId} 
            designDocuments={moduleData.designDocuments || []} 
            categories={moduleData.categories || {}} 
            onRefresh={() => fetchModuleData("design")} 
            canWrite={canWrite} 
            token={token} 
          />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "documents"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              Other Documents
            </DialogTitle>
          </DialogHeader>
          <OtherDocumentsContent projectId={projectId} documents={moduleData.documents || []} onRefresh={() => fetchModuleData("documents")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "boq"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-pink-600" />
              BOQ & Quotations
            </DialogTitle>
          </DialogHeader>
          <BOQContent projectId={projectId} boqs={moduleData.boqs || []} quotations={moduleData.quotations || []} onRefresh={() => fetchModuleData("boq")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "legal"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-indigo-600" />
              Legal Documents
            </DialogTitle>
          </DialogHeader>
          <LegalDocumentsContent 
            projectId={projectId} 
            vendorContracts={moduleData.vendorContracts || []} 
            clientAgreements={moduleData.clientAgreements || []} 
            completionChecklists={moduleData.completionChecklists || []}
            onRefresh={() => fetchModuleData("legal")} 
            canWrite={canWrite} 
            token={token} 
          />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "timeline"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5 text-orange-600" />
              Timeline & Milestones
            </DialogTitle>
          </DialogHeader>
          <TimelineContent projectId={projectId} project={project} milestones={moduleData.milestones || []} onRefresh={() => fetchModuleData("timeline")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "payments"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-green-600" />
              Payments & Finance
            </DialogTitle>
          </DialogHeader>
          <PaymentsContent projectId={projectId} expenses={moduleData.expenses || []} invoices={moduleData.invoices || []} payments={moduleData.payments || []} onRefresh={() => fetchModuleData("payments")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "vendors"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-cyan-600" />
              Vendors
            </DialogTitle>
          </DialogHeader>
          <VendorsContent projectId={projectId} vendors={moduleData.vendors || []} onRefresh={() => fetchModuleData("vendors")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "materials"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="h-5 w-5 text-amber-600" />
              Materials & Procurement
            </DialogTitle>
          </DialogHeader>
          <MaterialsContent projectId={projectId} materials={moduleData.materials || []} onRefresh={() => fetchModuleData("materials")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "daily"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-indigo-600" />
              Daily Updates
            </DialogTitle>
          </DialogHeader>
          <DailyUpdatesContent projectId={projectId} updates={moduleData.updates || []} onRefresh={() => fetchModuleData("daily")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "risks"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-red-600" />
              Risk Management
            </DialogTitle>
          </DialogHeader>
          <RisksContent projectId={projectId} risks={moduleData.risks || []} onRefresh={() => fetchModuleData("risks")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      <Dialog open={activeModule === "progress"} onOpenChange={() => closeModule()}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-teal-600" />
              Site Progress
            </DialogTitle>
          </DialogHeader>
          <SiteProgressContent projectId={projectId} projectName={project?.name} siteProgress={moduleData.siteProgress || []} onRefresh={() => fetchModuleData("progress")} canWrite={canWrite} token={token} />
        </DialogContent>
      </Dialog>

      {/* Progress Report Dialog */}
      <Dialog open={progressReportOpen} onOpenChange={setProgressReportOpen}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-brillien-600" />
              Project Progress Report
            </DialogTitle>
          </DialogHeader>
          {progressReportData && (
            <div className="space-y-6">
              {/* Report Header */}
              <div className="bg-gradient-to-r from-brillien-50 to-brillien-100 p-4 rounded-lg">
                <h3 className="font-bold text-lg text-brillien-800">{project?.name}</h3>
                <p className="text-sm text-brillien-600">Report Generated: {new Date().toLocaleString()}</p>
                <div className="flex gap-4 mt-2 text-sm text-slate-600">
                  <span>ID: {project?.id}</span>
                  <span>Client: {project?.client}</span>
                  <span>Location: {project?.city}, {project?.state}</span>
                </div>
              </div>

              {/* Summary Stats */}
              <div className="grid grid-cols-4 gap-4">
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <p className="text-xs font-medium text-blue-600 uppercase">Overall Progress</p>
                  <p className="text-2xl font-bold text-blue-800">{progressReportData.stats.avgCompletion}%</p>
                  <Progress value={progressReportData.stats.avgCompletion} className="h-2 mt-2" />
                </Card>
                <Card className="p-4 bg-emerald-50 border-emerald-200">
                  <p className="text-xs font-medium text-emerald-600 uppercase">Milestones</p>
                  <p className="text-2xl font-bold text-emerald-800">
                    {progressReportData.stats.completedMilestones}/{progressReportData.stats.totalMilestones}
                  </p>
                  <p className="text-xs text-emerald-600 mt-1">Completed</p>
                </Card>
                <Card className="p-4 bg-orange-50 border-orange-200">
                  <p className="text-xs font-medium text-orange-600 uppercase">Budget Utilization</p>
                  <p className="text-2xl font-bold text-orange-800">
                    {progressReportData.stats.budget > 0 
                      ? Math.round((progressReportData.stats.totalExpenses / progressReportData.stats.budget) * 100) 
                      : 0}%
                  </p>
                  <p className="text-xs text-orange-600 mt-1">
                    ₹{(progressReportData.stats.totalExpenses / 100000).toFixed(2)}L / ₹{(progressReportData.stats.budget / 100000).toFixed(2)}L
                  </p>
                </Card>
                <Card className="p-4 bg-red-50 border-red-200">
                  <p className="text-xs font-medium text-red-600 uppercase">Open Risks</p>
                  <p className="text-2xl font-bold text-red-800">{progressReportData.stats.openRisks}</p>
                  <p className="text-xs text-red-600 mt-1">of {progressReportData.stats.totalRisks} total</p>
                </Card>
              </div>

              {/* Milestones Section */}
              <div>
                <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Milestone Status
                </h4>
                {progressReportData.milestones.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">No milestones defined</p>
                ) : (
                  <div className="space-y-2">
                    {progressReportData.milestones.map(ms => (
                      <div key={ms.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          {ms.status === 'Completed' ? (
                            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                          ) : (
                            <Clock className="h-5 w-5 text-slate-400" />
                          )}
                          <div>
                            <p className="font-medium text-slate-900">{ms.name}</p>
                            <p className="text-xs text-slate-500">Planned: {ms.planned_date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Badge className={statusColors[ms.status] || 'bg-slate-100 text-slate-600'}>{ms.status}</Badge>
                          <span className="text-sm font-medium">{ms.completion_percent || 0}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Open Risks Section */}
              {progressReportData.stats.openRisks > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-red-600" />
                    Open Risks
                  </h4>
                  <div className="space-y-2">
                    {progressReportData.risks.filter(r => r.status === 'Open' || r.status === 'Mitigating').map(risk => (
                      <div key={risk.id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                        <div>
                          <p className="font-medium text-slate-900">{risk.title}</p>
                          <p className="text-xs text-slate-500">{risk.risk_type}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge className={`${
                            risk.impact_level === 'High' ? 'bg-red-100 text-red-700' :
                            risk.impact_level === 'Medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {risk.impact_level}
                          </Badge>
                          <Badge className={statusColors[risk.status]}>{risk.status}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Recent Site Progress */}
              {progressReportData.siteProgress.length > 0 && (
                <div>
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-teal-600" />
                    Recent Site Progress (Last 5)
                  </h4>
                  <div className="space-y-2">
                    {progressReportData.siteProgress.slice(0, 5).map(sp => (
                      <div key={sp.id} className="p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-mono text-sm text-slate-600">{sp.date}</span>
                          <span className="text-sm font-medium text-teal-600">{sp.completion_percent}% Complete</span>
                        </div>
                        <p className="text-sm text-slate-700">{sp.scope_of_work}</p>
                        <div className="flex gap-4 mt-1 text-xs text-slate-500">
                          <span>Workers: {sp.no_of_workers}</span>
                          {sp.challenges_faced && <span className="text-amber-600">Challenges: {sp.challenges_faced}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Download PDF Button */}
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button 
                  variant="outline" 
                  onClick={async () => {
                    try {
                      const response = await axios.get(`${API}/projects/${projectId}/progress-report/pdf`, {
                        headers: { Authorization: `Bearer ${token}` },
                        responseType: 'blob'
                      });
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', `Progress_Report_${project?.id || 'PRJ'}_${new Date().toISOString().slice(0,10)}.pdf`);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                      toast.success("Progress Report PDF downloaded");
                    } catch (error) {
                      toast.error("Failed to download PDF");
                    }
                  }}
                  className="bg-brillien-600 hover:bg-brillien-700 text-white"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download PDF
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// BasicDetailsContent - imported from @/components/project-detail/BasicDetailsContent

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-2">
      <span className="text-sm text-slate-500">{label}</span>
      <span className="text-sm font-medium text-slate-900">{value}</span>
    </div>
  );
}

// OtherDocumentsContent - imported from @/components/project-detail/OtherDocumentsContent

// ============ NEW BOQ TEMPLATE CONTENT ============
// ============ DAILY UPDATES CONTENT ============
function DailyUpdatesContent({ projectId, updates, onRefresh, canWrite, token }) {
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canWrite && <Button size="sm" className="bg-brillien-600 hover:bg-brillien-700"><Plus className="h-4 w-4 mr-1" />Add Update</Button>}
      </div>
      {updates.length === 0 ? (
        <p className="text-slate-500 text-center py-8">No daily updates recorded</p>
      ) : (
        <div className="space-y-3">
          {updates.map(u => (
            <Card key={u.id} className="p-4 border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <span className="font-mono text-sm text-slate-500">{u.date}</span>
                <Badge className={statusColors[u.work_status]}>{u.work_status}</Badge>
              </div>
              <p className="text-slate-700">{u.work_summary}</p>
              <div className="flex gap-4 mt-2 text-sm text-slate-500">
                <span>Labour: {u.labour_count}</span>
                <span>Supervisors: {u.supervisor_count}</span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ CHANGE ORDERS CONTENT ============
function ChangeOrdersContent({ projectId, changeOrders, variations, onRefresh, canWrite, token }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-900">Change Orders</h4>
          {canWrite && <Button size="sm" className="bg-brillien-600 hover:bg-brillien-700"><Plus className="h-4 w-4 mr-1" />Create</Button>}
        </div>
        {changeOrders.length === 0 ? (
          <p className="text-slate-500 text-center py-4">No change orders</p>
        ) : (
          <div className="space-y-3">
            {changeOrders.map(co => (
              <Card key={co.id} className="p-4 border-slate-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-slate-900">{co.description}</p>
                    <p className="text-sm text-slate-500">{co.change_type}</p>
                  </div>
                  <div className="text-right">
                    <p className={`font-bold ${co.variance >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                      {co.variance >= 0 ? "+" : ""}₹{co.variance.toLocaleString()}
                    </p>
                    <Badge className={statusColors[co.status]}>{co.status}</Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-slate-900">Variations</h4>
          {canWrite && <Button size="sm" className="bg-brillien-600 hover:bg-brillien-700"><Plus className="h-4 w-4 mr-1" />Add</Button>}
        </div>
        {variations.length === 0 ? (
          <p className="text-slate-500 text-center py-4">No variations</p>
        ) : (
          <div className="space-y-3">
            {variations.map(v => (
              <Card key={v.id} className="p-4 border-slate-200">
                <div className="flex items-center justify-between">
                  <p className="font-medium text-slate-900">{v.description}</p>
                  <div className="text-right">
                    <p className="font-bold text-slate-900">₹{v.estimated_cost.toLocaleString()}</p>
                    <Badge className={statusColors[v.status] || statusColors["Pending"]}>{v.status}</Badge>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ RISKS CONTENT ============
function RisksContent({ projectId, risks, onRefresh, canWrite, token }) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingRisk, setEditingRisk] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    risk_type: "Site access issues",
    description: "",
    impact_level: "Medium",
    mitigation_plan: "",
    status: "Open"
  });

  const riskTypes = [
    "Site access issues",
    "Material delays or shortages",
    "Vendor or labour unavailability",
    "Design changes or client approval delays",
    "Safety incidents",
    "Weather or external constraints",
    "Pending payments affecting progress",
    "Any issue stopping work execution"
  ];

  const impactColors = {
    "Low": "bg-slate-100 text-slate-700",
    "Medium": "bg-amber-100 text-amber-700",
    "High": "bg-orange-100 text-orange-700",
    "Critical": "bg-red-100 text-red-700",
  };

  const resetForm = () => {
    setFormData({
      title: "",
      risk_type: "Site access issues",
      description: "",
      impact_level: "Medium",
      mitigation_plan: "",
      status: "Open"
    });
    setEditingRisk(null);
    setShowForm(false);
  };

  const handleEditRisk = (risk) => {
    setFormData({
      title: risk.title || "",
      risk_type: risk.risk_type || "Site access issues",
      description: risk.description || "",
      impact_level: risk.impact_level || "Medium",
      mitigation_plan: risk.mitigation_plan || "",
      status: risk.status || "Open"
    });
    setEditingRisk(risk);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title) {
      toast.error("Risk title is required");
      return;
    }
    setSubmitting(true);
    try {
      if (editingRisk) {
        await axios.put(`${API}/projects/${projectId}/risks/${editingRisk.id}`, null, {
          params: formData,
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Risk updated successfully");
      } else {
        await axios.post(`${API}/projects/${projectId}/risks`, null, {
          params: formData,
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Risk added successfully");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      const detail = error.response?.data?.detail;
      let errorMsg = "Failed to save risk";
      if (typeof detail === 'string') {
        errorMsg = detail;
      } else if (Array.isArray(detail) && detail.length > 0) {
        errorMsg = detail[0]?.msg || detail[0]?.message || errorMsg;
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteRisk = async (riskId) => {
    if (!window.confirm("Delete this risk?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/risks/${riskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Risk deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete risk");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        {canWrite && !showForm && (
          <Button size="sm" onClick={() => setShowForm(true)} className="bg-brillien-600 hover:bg-brillien-700" data-testid="add-risk-btn">
            <Plus className="h-4 w-4 mr-1" />Add Risk
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="p-4 border-brillien-200 bg-brillien-50/50">
          <div className="flex justify-between items-center mb-4">
            <h4 className="font-semibold">{editingRisk ? "Edit Risk" : "Add New Risk"}</h4>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Risk Title *</Label>
                <Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} required placeholder="Brief title for the risk" />
              </div>
              <div>
                <Label>Risk Type *</Label>
                <Select value={formData.risk_type} onValueChange={(v) => setFormData({...formData, risk_type: v})}>
                  <SelectTrigger><SelectValue placeholder="Select risk type" /></SelectTrigger>
                  <SelectContent>
                    {riskTypes.map((type) => (
                      <SelectItem key={type} value={type}>{type}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Detailed description of the risk" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Impact Level</Label>
                <Select value={formData.impact_level} onValueChange={(v) => setFormData({...formData, impact_level: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Low">Low</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="High">High</SelectItem>
                    <SelectItem value="Critical">Critical</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Mitigated">Mitigated</SelectItem>
                    <SelectItem value="Closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Mitigation Plan</Label>
              <Textarea value={formData.mitigation_plan} onChange={(e) => setFormData({...formData, mitigation_plan: e.target.value})} rows={2} placeholder="Steps to mitigate this risk" />
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">
                {submitting ? "Saving..." : editingRisk ? "Update Risk" : "Add Risk"}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {risks.length === 0 && !showForm ? (
        <p className="text-slate-500 text-center py-8">No risks identified</p>
      ) : !showForm && (
        <div className="space-y-3">
          {risks.map(r => (
            <Card key={r.id} className={`p-4 border-l-4 ${r.impact_level === "Critical" ? "border-l-red-500" : r.impact_level === "High" ? "border-l-orange-500" : "border-l-amber-500"}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h5 className="font-medium text-slate-900">{r.title}</h5>
                  <p className="text-sm text-slate-500">{r.description}</p>
                  <p className="text-xs text-slate-400 mt-1">Type: {r.risk_type}</p>
                  <div className="flex gap-4 mt-2 text-xs text-slate-400">
                    {r.created_at && (
                      <span>Created: {new Date(r.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    )}
                    {r.updated_at && (
                      <span>Updated: {new Date(r.updated_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={impactColors[r.impact_level]}>{r.impact_level}</Badge>
                  <Badge className={statusColors[r.status]}>{r.status}</Badge>
                  {canWrite && (
                    <>
                      <Button variant="ghost" size="sm" onClick={() => handleEditRisk(r)} className="text-blue-600"><Edit className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteRisk(r.id)} className="text-red-600"><Trash2 className="h-4 w-4" /></Button>
                    </>
                  )}
                </div>
              </div>
              {r.mitigation_plan && (
                <p className="text-sm text-blue-700 bg-blue-50 p-2 rounded mt-3">
                  <strong>Mitigation:</strong> {r.mitigation_plan}
                </p>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ DESIGN DOCUMENTS CONTENT ============
const DESIGN_CATEGORIES = {
  site_details: {
    name: "Site Details",
    icon: MapPin,
    color: "bg-blue-100 text-blue-700",
    subcategories: [
      "Site Measurement Drawings",
      "Site Photographs",
      "Site Video Walkthrough",
      "References for graphic designer"
    ]
  },
  "2d_drawings": {
    name: "2D Drawings",
    icon: FileText,
    color: "bg-purple-100 text-purple-700",
    subcategories: [
      "2D Layout",
      "MEP Drawings",
      "Detail Drawings"
    ]
  },
  "3d_designs": {
    name: "3D Designs",
    icon: Image,
    color: "bg-pink-100 text-pink-700",
    subcategories: [
      "Renders & PDF",
      "Graphic designer PDF"
    ]
  },
  materials: {
    name: "Materials",
    icon: Package,
    color: "bg-amber-100 text-amber-700",
    subcategories: [
      "Used materials"
    ]
  },
  final_approvals: {
    name: "Final Approvals",
    icon: Shield,
    color: "bg-emerald-100 text-emerald-700",
    subcategories: [
      "Final Approved 2D",
      "Final Approved 3D",
      "Final approved Graphic designs"
    ]
  }
};

function DesignDocumentsContent({ projectId, designDocuments, categories, onRefresh, canWrite, token }) {
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadData, setUploadData] = useState({ 
    name: "", 
    description: "", 
    category: "", 
    subcategory: "",
    files: []  // Changed from file to files for multiple upload
  });

  const getDocsBySubcategory = (category, subcategory) => {
    return designDocuments.filter(doc => doc.category === category && doc.subcategory === subcategory);
  };

  const getCategoryCount = (categoryKey) => {
    return designDocuments.filter(doc => doc.category === categoryKey).length;
  };

  // Handle design document download with authentication
  const handleDocumentDownload = async (docId, fileName, originalFilename) => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}/design-documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Use original_filename for correct extension, fallback to fileName
      const downloadName = originalFilename || fileName || 'document';
      
      // Get the content type from response to determine file type
      const contentType = response.headers['content-type'];
      
      // Check if response is an error (JSON instead of file)
      if (contentType && contentType.includes('application/json')) {
        const text = await response.data.text();
        try {
          const error = JSON.parse(text);
          toast.error(error.detail || "Failed to download document");
        } catch {
          toast.error("Failed to download document");
        }
        return;
      }
      
      // Verify we got actual file content
      if (!response.data || response.data.size === 0) {
        toast.error("Downloaded file is empty");
        return;
      }
      
      // Create download link
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', downloadName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch (error) {
      console.error("Download error:", error);
      
      // Handle blob error response
      let errorMessage = "Failed to download document";
      if (error.response?.data) {
        try {
          // Try to read blob as text if it's a JSON error
          if (error.response.data instanceof Blob) {
            const text = await error.response.data.text();
            const parsed = JSON.parse(text);
            errorMessage = parsed.detail || errorMessage;
          } else {
            errorMessage = error.response.data.detail || errorMessage;
          }
        } catch {
          // Keep default error message
        }
      }
      
      if (errorMessage.includes("re-upload") || errorMessage.includes("not found")) {
        toast.error("File not found on server. This document may need to be re-uploaded.", {
          duration: 5000
        });
      } else {
        toast.error(errorMessage);
      }
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadData.files.length || !uploadData.category || !uploadData.subcategory) {
      toast.error("Please select files and category/subcategory");
      return;
    }

    setUploading(true);
    let uploadedCount = 0;
    let failedCount = 0;
    
    // Upload files one by one
    for (const file of uploadData.files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("category", uploadData.category);
      formData.append("subcategory", uploadData.subcategory);
      formData.append("name", uploadData.name || file.name.replace(/\.[^/.]+$/, ""));
      if (uploadData.description) {
        formData.append("description", uploadData.description);
      }

      try {
        await axios.post(`${API}/projects/${projectId}/design-documents`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data"
          }
        });
        uploadedCount++;
      } catch (error) {
        console.error(`Failed to upload ${file.name}:`, error);
        failedCount++;
      }
    }
    
    if (uploadedCount > 0) {
      toast.success(`${uploadedCount} file(s) uploaded successfully${failedCount > 0 ? `, ${failedCount} failed` : ''}`);
      setShowUploadForm(false);
      setUploadData({ name: "", description: "", category: "", subcategory: "", files: [] });
      onRefresh();
    } else {
      toast.error("Failed to upload files");
    }
    setUploading(false);
  };

  const handleDelete = async (docId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;
    
    try {
      await axios.delete(`${API}/projects/${projectId}/design-documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "N/A";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (fileType) => {
    if (!fileType) return File;
    if (fileType.includes("image")) return Image;
    if (fileType.includes("video")) return Video;
    if (fileType.includes("pdf")) return FileText;
    return File;
  };

  return (
    <div className="space-y-4">
      {/* Header with upload button */}
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-500">
          Total Documents: <span className="font-semibold text-slate-900">{designDocuments.length}</span>
        </div>
        {canWrite && (
          <Button 
            onClick={() => setShowUploadForm(true)} 
            className="bg-brillien-600 hover:bg-brillien-700"
            data-testid="upload-design-doc-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        )}
      </div>

      {/* Upload Form Modal */}
      {showUploadForm && (
        <Card className="p-4 border-brillien-200 bg-brillien-50" data-testid="upload-design-form">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900">Upload Design Document</h4>
            <Button variant="ghost" size="sm" onClick={() => setShowUploadForm(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category *</Label>
                <Select 
                  value={uploadData.category} 
                  onValueChange={(val) => setUploadData({...uploadData, category: val, subcategory: ""})}
                >
                  <SelectTrigger data-testid="upload-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(DESIGN_CATEGORIES).map(([key, cat]) => (
                      <SelectItem key={key} value={key}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Subcategory *</Label>
                <Select 
                  value={uploadData.subcategory} 
                  onValueChange={(val) => setUploadData({...uploadData, subcategory: val})}
                  disabled={!uploadData.category}
                >
                  <SelectTrigger data-testid="upload-subcategory-select">
                    <SelectValue placeholder="Select subcategory" />
                  </SelectTrigger>
                  <SelectContent>
                    {uploadData.category && DESIGN_CATEGORIES[uploadData.category]?.subcategories.map((subcat) => (
                      <SelectItem key={subcat} value={subcat}>{subcat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Document Name *</Label>
              <Input 
                value={uploadData.name} 
                onChange={(e) => setUploadData({...uploadData, name: e.target.value})}
                placeholder="Enter document name"
                data-testid="upload-doc-name"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea 
                value={uploadData.description} 
                onChange={(e) => setUploadData({...uploadData, description: e.target.value})}
                placeholder="Optional description"
                rows={2}
                data-testid="upload-doc-description"
              />
            </div>
            <div>
              <Label>Files * (Select multiple)</Label>
              <Input 
                type="file" 
                multiple
                onChange={(e) => setUploadData({...uploadData, files: Array.from(e.target.files)})}
                className="cursor-pointer"
                data-testid="upload-doc-file"
              />
              {uploadData.files.length > 0 && (
                <p className="text-xs text-slate-500 mt-1">
                  Selected: {uploadData.files.length} file(s) - {uploadData.files.map(f => f.name).join(', ')}
                </p>
              )}
            </div>
            <div className="flex gap-2">
              <Button type="submit" disabled={uploading} className="bg-brillien-600 hover:bg-brillien-700" data-testid="upload-doc-submit">
                {uploading ? "Uploading..." : "Upload"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowUploadForm(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      {/* Categories Accordion */}
      <div className="space-y-2">
        {Object.entries(DESIGN_CATEGORIES).map(([catKey, catData]) => {
          const Icon = catData.icon;
          const count = getCategoryCount(catKey);
          const isExpanded = expandedCategory === catKey;

          return (
            <Card key={catKey} className="border-slate-200 overflow-hidden" data-testid={`design-category-${catKey}`}>
              {/* Category Header */}
              <div 
                className={`p-4 cursor-pointer hover:bg-slate-50 transition-colors flex items-center justify-between ${isExpanded ? "bg-slate-50 border-b" : ""}`}
                onClick={() => setExpandedCategory(isExpanded ? null : catKey)}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${catData.color}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <h4 className="font-medium text-slate-900">{catData.name}</h4>
                    <p className="text-xs text-slate-500">{count} document{count !== 1 ? "s" : ""}</p>
                  </div>
                </div>
                {isExpanded ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
              </div>

              {/* Subcategories */}
              {isExpanded && (
                <div className="bg-white">
                  {catData.subcategories.map((subcat) => {
                    const docs = getDocsBySubcategory(catKey, subcat);
                    const isSelected = selectedSubcategory === `${catKey}-${subcat}`;

                    return (
                      <div key={subcat} className="border-b last:border-b-0">
                        <div 
                          className={`px-4 py-3 pl-16 cursor-pointer hover:bg-slate-50 flex items-center justify-between ${isSelected ? "bg-fuchsia-50" : ""}`}
                          onClick={() => setSelectedSubcategory(isSelected ? null : `${catKey}-${subcat}`)}
                        >
                          <div className="flex items-center gap-2">
                            <FolderOpen className="h-4 w-4 text-slate-400" />
                            <span className="text-sm font-medium text-slate-700">{subcat}</span>
                          </div>
                          <Badge variant="outline" className="text-xs">
                            {docs.length}
                          </Badge>
                        </div>

                        {/* Documents in subcategory */}
                        {isSelected && (
                          <div className="px-4 py-3 pl-20 bg-slate-50 space-y-2">
                            {docs.length === 0 ? (
                              <p className="text-xs text-slate-500 italic">No documents uploaded</p>
                            ) : (
                              docs.map(doc => {
                                const FileIcon = getFileIcon(doc.file_type);
                                return (
                                  <div key={doc.id} className="flex items-center justify-between bg-white p-3 rounded-lg border border-slate-200">
                                    <div className="flex items-center gap-3">
                                      <FileIcon className="h-5 w-5 text-slate-500" />
                                      <div>
                                        <p className="text-sm font-medium text-slate-900">{doc.name}</p>
                                        <p className="text-xs text-slate-500">
                                          {formatFileSize(doc.file_size)} • {doc.uploaded_by} • {new Date(doc.uploaded_at).toLocaleDateString()}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-1">
                                      {doc.file_available === false ? (
                                        <span className="text-xs text-red-500 px-2 py-1 bg-red-50 rounded" title="File not found on server. Please re-upload.">
                                          File Missing
                                        </span>
                                      ) : (
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          onClick={() => handleDocumentDownload(doc.id, doc.file_name || doc.name, doc.original_filename)}
                                          data-testid={`download-doc-${doc.id}`}
                                        >
                                          <Download className="h-4 w-4" />
                                        </Button>
                                      )}
                                      {canWrite && (
                                        <Button 
                                          variant="ghost" 
                                          size="sm" 
                                          onClick={() => handleDelete(doc.id)}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                          data-testid={`delete-doc-${doc.id}`}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      )}
                                    </div>
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Empty state */}
      {designDocuments.length === 0 && !showUploadForm && (
        <div className="text-center py-12 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
          <Palette className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="text-slate-500 mb-2">No design documents uploaded yet</p>
          <p className="text-xs text-slate-400">Click &quot;Upload Document&quot; to add your first design file</p>
        </div>
      )}
    </div>
  );
}

// ============ SITE PROGRESS CONTENT ============
function SiteProgressContent({ projectId, projectName, siteProgress, onRefresh, canWrite, token }) {
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5),
    scope_of_work: "",
    no_of_workers: "",
    completion_percent: "",
    challenges_faced: ""
  });

  const resetForm = () => {
    setFormData({
      date: new Date().toISOString().split('T')[0],
      time: new Date().toTimeString().slice(0, 5),
      scope_of_work: "",
      no_of_workers: "",
      completion_percent: "",
      challenges_faced: ""
    });
    setEditingId(null);
    setShowForm(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        project_name: projectName,
        no_of_workers: parseInt(formData.no_of_workers) || 0,
        completion_percent: parseFloat(formData.completion_percent) || 0
      };

      if (editingId) {
        await axios.put(`${API}/projects/${projectId}/site-progress/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Site progress updated!");
      } else {
        await axios.post(`${API}/projects/${projectId}/site-progress`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Site progress added!");
      }
      resetForm();
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save site progress");
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setFormData({
      date: record.date,
      time: record.time || "09:00",
      scope_of_work: record.scope_of_work || "",
      no_of_workers: record.no_of_workers?.toString() || "",
      completion_percent: record.completion_percent?.toString() || "",
      challenges_faced: record.challenges_faced || ""
    });
    setEditingId(record.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/site-progress/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Record deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
  };

  return (
    <div className="space-y-4">
      {canWrite && (
        <div className="flex justify-end">
          <Button 
            onClick={() => { resetForm(); setShowForm(!showForm); }} 
            className="bg-brillien-600 hover:bg-brillien-700"
            data-testid="add-site-progress-btn"
          >
            <Plus className="h-4 w-4 mr-2" />Add Progress Record
          </Button>
        </div>
      )}

      {showForm && (
        <Card className="p-4 border-brillien-200 bg-brillien-50">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-slate-900">{editingId ? "Edit Progress Record" : "Add Progress Record"}</h4>
            <Button variant="ghost" size="sm" onClick={resetForm}><X className="h-4 w-4" /></Button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label>Project Name</Label>
                <Input value={projectName} disabled className="bg-slate-100" />
              </div>
              <div>
                <Label>Date *</Label>
                <Input 
                  type="date" 
                  value={formData.date} 
                  onChange={(e) => setFormData({...formData, date: e.target.value})}
                  required
                  data-testid="progress-date"
                />
              </div>
              <div>
                <Label>Time *</Label>
                <Input 
                  type="time" 
                  value={formData.time} 
                  onChange={(e) => setFormData({...formData, time: e.target.value})}
                  required
                  data-testid="progress-time"
                />
              </div>
              <div>
                <Label>No. of Workers *</Label>
                <Input 
                  type="number" 
                  value={formData.no_of_workers} 
                  onChange={(e) => setFormData({...formData, no_of_workers: e.target.value})}
                  placeholder="Enter count"
                  required
                  data-testid="progress-workers"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scope of Work *</Label>
                <Textarea 
                  value={formData.scope_of_work} 
                  onChange={(e) => setFormData({...formData, scope_of_work: e.target.value})}
                  placeholder="Describe the work done"
                  rows={3}
                  required
                  data-testid="progress-scope"
                />
              </div>
              <div>
                <Label>Challenges Faced</Label>
                <Textarea 
                  value={formData.challenges_faced} 
                  onChange={(e) => setFormData({...formData, challenges_faced: e.target.value})}
                  placeholder="Any challenges or issues faced"
                  rows={3}
                  data-testid="progress-challenges"
                />
              </div>
            </div>
            <div className="w-48">
              <Label>% of Completion *</Label>
              <Input 
                type="number" 
                min="0" 
                max="100" 
                step="0.1"
                value={formData.completion_percent} 
                onChange={(e) => setFormData({...formData, completion_percent: e.target.value})}
                placeholder="0-100"
                required
                data-testid="progress-completion"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button type="submit" disabled={submitting} className="bg-brillien-600 hover:bg-brillien-700">
                {submitting ? "Saving..." : editingId ? "Update" : "Save"}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {siteProgress.length === 0 ? (
        <div className="text-center py-8 text-slate-500">
          <TrendingUp className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p>No site progress records yet</p>
          {canWrite && <p className="text-sm mt-1">Click "Add Progress Record" to create one</p>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold w-16">S.No</TableHead>
                <TableHead className="font-semibold">Project Name</TableHead>
                <TableHead className="font-semibold">Date</TableHead>
                <TableHead className="font-semibold">Time</TableHead>
                <TableHead className="font-semibold">Scope of Work</TableHead>
                <TableHead className="font-semibold text-center">Workers</TableHead>
                <TableHead className="font-semibold text-center">% Completion</TableHead>
                <TableHead className="font-semibold">Challenges</TableHead>
                <TableHead className="font-semibold">Created</TableHead>
                <TableHead className="font-semibold">Updated</TableHead>
                <TableHead className="text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {siteProgress.map((record, index) => (
                <TableRow key={record.id} data-testid={`progress-row-${record.id}`}>
                  <TableCell className="font-mono text-sm text-slate-600">{index + 1}</TableCell>
                  <TableCell className="font-medium text-slate-900">{record.project_name || projectName}</TableCell>
                  <TableCell className="text-sm text-slate-700">{formatDate(record.date)}</TableCell>
                  <TableCell className="text-sm text-slate-700">{record.time || "-"}</TableCell>
                  <TableCell className="text-sm text-slate-700 max-w-xs truncate" title={record.scope_of_work}>{record.scope_of_work}</TableCell>
                  <TableCell className="text-center font-mono text-sm">{record.no_of_workers}</TableCell>
                  <TableCell className="text-center">
                    <Badge className={`${record.completion_percent >= 80 ? "bg-emerald-100 text-emerald-700" : record.completion_percent >= 50 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-700"}`}>
                      {record.completion_percent}%
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-slate-700 max-w-xs truncate" title={record.challenges_faced}>{record.challenges_faced || "-"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(record.created_at)}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(record.updated_at)}</TableCell>
                  <TableCell className="text-right">
                    {canWrite && (
                      <div className="flex justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleEdit(record)}
                          className="text-brillien-600 hover:text-brillien-700"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDelete(record.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}